import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, MoreHorizontal, Pencil, Plus, Power, Router, Trash2 } from 'lucide-react';
import { config } from '../config';
import { useAuth } from '../hooks/useAuth';
import { useDevices, type DeviceSummary } from '../hooks/useDevices';
import ConfirmationModal from './ConfirmationModal';
import DeviceActivateForm from './DeviceActivateForm';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppSheet } from '@/components/ui/app-sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { apiErrorMessage } from '@/utils/apiError';
import { deviceTypeLabel } from '@/types/deviceType';
import { canonicalIp } from '@/utils/netValidation';

/**
 * NetworkEquipmentManager — the full management surface for network
 * equipment (managed devices): list + add/activate/edit/delete. Rendered
 * by My Network's "Network equipment" tab; the "All" tab keeps a slim
 * read-only table that links here instead.
 *
 * `search` filters client-side over the same fields as the slim table so
 * the two views agree on what matches.
 */

const STATUS_STYLE: Record<string, string> = {
  online: 'bg-sev-4/15 text-sev-4',
  offline: 'bg-sev-1/15 text-sev-1',
  unknown: 'bg-muted text-muted-foreground',
};

interface EditDraft {
  id: string;
  name: string;
  description: string | null;
}

export default function NetworkEquipmentManager({ search }: { search: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const organizationId = user?.organizationId;
  const backendApi = config.backendApi;

  const { data: devices, isLoading, error } = useDevices(organizationId ?? undefined);

  const [isCreateModalActive, setIsCreateModalActive] = useState(false);
  const [editingDevice, setEditingDevice] = useState<EditDraft | null>(null);
  const [deletingDevice, setDeletingDevice] = useState<DeviceSummary | null>(null);
  const [provisioningDevice, setProvisioningDevice] = useState<DeviceSummary | null>(null);

  const updateDeviceMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    Error,
    EditDraft
  >({
    mutationFn: async ({ id, name, description }) => {
      await axios.put(
        `${backendApi}/api/devices/${id}`,
        { name, description },
        { withCredentials: true },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      setEditingDevice(null);
    },
    onError: (err) => {
      console.error('Failed to update device:', err);
    },
  });

  const deleteDeviceMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    Error,
    string
  >({
    mutationFn: async (deviceId) => {
      await axios.delete(`${backendApi}/api/devices/${deviceId}`, { withCredentials: true });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      setDeletingDevice(null);
    },
    // A delete can be refused: a controller-sourced device answers 409, naming
    // the integration that owns it and the way out. The dialog stays up (see the
    // confirm handler) so the reason lands somewhere the operator is looking.
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Failed to delete device.'));
    },
  });

  const filtered = useMemo<DeviceSummary[]>(() => {
    if (!devices) return [];
    if (!search) return devices;
    const lower = search.toLowerCase();
    // A whole address typed in another spelling still finds the stored one.
    const ip = canonicalIp(search) ?? lower;
    return devices.filter(
      (d) =>
        d.name.toLowerCase().includes(lower) ||
        (d.ipAddress?.toLowerCase().includes(ip) ?? false) ||
        d.deviceType.toLowerCase().includes(lower) ||
        d.regionName.toLowerCase().includes(lower),
    );
  }, [devices, search]);

  const handleUpdateDeviceSubmit = (event: React.SubmitEvent) => {
    event.preventDefault();
    if (!editingDevice) return;
    updateDeviceMutation.mutate(editingDevice);
  };

  const columns = useMemo<readonly ResponsiveTableColumn<DeviceSummary>[]>(
    () => [
      {
        key: 'name',
        label: 'Name',
        hideMobileLabel: true,
        render: (d) => (
          <div className="min-w-0">
            <span className="flex items-center gap-2 min-w-0">
              <Link
                to={`/devices/${d.id}`}
                className="font-medium hover:underline truncate"
                onClick={(e) => { e.stopPropagation(); }}
              >
                {d.name || '[Unnamed]'}
              </Link>
              {d.pollingPaused ? (
                <span
                  className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-sev-3-soft text-sev-3 whitespace-nowrap"
                  title="Polling is paused for this device — either an operator held it off, or the account is over its plan's device limit. The device is still counted."
                >
                  Monitoring paused
                </span>
              ) : null}
            </span>
            {d.description ? (
              <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                {d.description}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        hideAt: 'mobile',
        render: (d) => (
          <Badge variant="secondary" className="font-normal">
            {deviceTypeLabel(d.deviceType)}
          </Badge>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        render: (d) => (
          <Badge
            className={cn(
              'border-0 font-normal capitalize',
              STATUS_STYLE[d.status] ?? 'bg-muted text-muted-foreground',
            )}
          >
            {d.status}
          </Badge>
        ),
      },
      {
        key: 'ip',
        label: 'IP address',
        render: (d) => (
          <code className="font-mono text-xs">{d.ipAddress || '—'}</code>
        ),
      },
      {
        key: 'region',
        label: 'Region',
        hideAt: 'mobile',
        render: (d) => <span className="text-xs">{d.regionName}</span>,
      },
      {
        key: 'division',
        label: 'Division',
        hideAt: 'mobile',
        render: (d) => (
          <span className="text-xs text-muted-foreground">{d.divisionName || '—'}</span>
        ),
      },
    ],
    [],
  );

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {String(filtered.length)}{' '}
          {filtered.length === 1 ? 'piece of equipment' : 'pieces of equipment'}
        </p>
        <Button size="sm" className="gap-1.5" onClick={() => { setIsCreateModalActive(true); }}>
          <Plus className="w-4 h-4" />
          Add equipment
        </Button>
      </div>

      <ResponsiveTable<DeviceSummary>
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        onRowClick={(r) => { void navigate(`/devices/${r.id}`); }}
        empty={
          !devices || devices.length === 0 ? (
            <EmptyState
              variant="no-data"
              icon={Router}
              title="No equipment yet"
              description="Add your first device to start managing your network."
              size="inline"
              action={
                <Button className="gap-1.5" onClick={() => { setIsCreateModalActive(true); }}>
                  <Plus className="w-4 h-4" />
                  Add equipment
                </Button>
              }
            />
          ) : (
            <EmptyState
              variant="no-data"
              title="No matches"
              description="No equipment matches your search."
              size="inline"
            />
          )
        }
        rowActions={(device) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); }}
                aria-label={`Actions for ${device.name}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {!device.snmpCredentialId ? (
                <DropdownMenuItem
                  onClick={() => { setProvisioningDevice(device); }}
                  className="gap-2 text-sev-4 focus:text-sev-4"
                >
                  <Power className="w-4 h-4" />
                  Activate
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() => {
                  updateDeviceMutation.reset();
                  setEditingDevice({
                    id: device.id,
                    name: device.name,
                    description: device.description,
                  });
                }}
                className="gap-2"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => { setDeletingDevice(device); }}
                className="gap-2 text-sev-1 focus:text-sev-1"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      />

      {/* Add device — the unified create/activate flow */}
      {isCreateModalActive && organizationId ? (
        <DeviceActivateForm
          organizationId={organizationId}
          onClose={() => { setIsCreateModalActive(false); }}
        />
      ) : null}

      {/* Edit device */}
      <AppSheet
        open={editingDevice !== null}
        onOpenChange={(open) => { if (!open) setEditingDevice(null); }}
        title="Edit device"
        size="md"
      >
        <AppSheet.Body>
          {updateDeviceMutation.isError ? (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {updateDeviceMutation.error.message || 'Failed to update device.'}
              </AlertDescription>
            </Alert>
          ) : null}
          {editingDevice ? (
            <form id="edit-device-form" onSubmit={handleUpdateDeviceSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="ed-name">Name</Label>
                <Input
                  id="ed-name"
                  type="text"
                  value={editingDevice.name}
                  onChange={(e) => {
                    setEditingDevice({ ...editingDevice, name: e.target.value });
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ed-desc">Description</Label>
                <Textarea
                  id="ed-desc"
                  value={editingDevice.description || ''}
                  onChange={(e) => {
                    setEditingDevice({ ...editingDevice, description: e.target.value });
                  }}
                />
              </div>
            </form>
          ) : null}
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            type="button"
            onClick={() => { setEditingDevice(null); }}
            disabled={updateDeviceMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-device-form"
            disabled={updateDeviceMutation.isPending}
          >
            {updateDeviceMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {/* Activate an already-known device */}
      {provisioningDevice && organizationId ? (
        <DeviceActivateForm
          organizationId={organizationId}
          deviceId={provisioningDevice.id}
          initial={{
            name: provisioningDevice.name,
            ipAddress: provisioningDevice.ipAddress ?? null,
            regionId: provisioningDevice.regionId,
            divisionId: provisioningDevice.divisionId,
            description: provisioningDevice.description ?? null,
          }}
          onClose={() => {
            setProvisioningDevice(null);
            void queryClient.invalidateQueries({ queryKey: ['devices'] });
          }}
        />
      ) : null}

      <ConfirmationModal
        isActive={deletingDevice !== null}
        title="Delete device"
        message={
          deletingDevice
            ? `Are you sure you want to delete "${deletingDevice.name || deletingDevice.ipAddress || 'this device'}"? This action cannot be undone.`
            : ''
        }
        confirmText="Delete device"
        cancelText="Cancel"
        onConfirm={(e) => {
          if (!deletingDevice) return;
          // Own the close so a refused delete does not read as a done one —
          // `onSuccess` clears `deletingDevice`, `onError` leaves it up.
          e.preventDefault();
          deleteDeviceMutation.mutate(deletingDevice.id);
        }}
        onCancel={() => { setDeletingDevice(null); }}
        isLoading={deleteDeviceMutation.isPending}
        isDanger={true}
      />
    </div>
  );
}
